import { type ReactNode, useEffect, useRef, useState } from 'react';
import { SidebarProvider, useSidebar } from '../../context/sidebar-context';
import { usePushSubscription } from '../../hooks/use-push-subscription';
import { cn } from '../../lib/utils';
import { getLoginRedirectPath, useAuthStore } from '../../stores/auth.store';
import { CommandPalette } from '../ui/command-palette';
import { Breadcrumb } from './breadcrumb';
import { Header } from './header';
import { Sidebar } from './sidebar';

const IDLE_TIMEOUT_MS = Number(import.meta.env.VITE_SESSION_IDLE_TIMEOUT_MINUTES ?? 30) * 60 * 1000;
const WARN_BEFORE_MS = 2 * 60 * 1000;

interface AppLayoutProps {
  children: ReactNode;
  noPadding?: boolean;
}

function LayoutContent({ children, noPadding }: AppLayoutProps) {
  const { sessionTimedOut, clearSession, user } = useAuthStore();
  const { isExpanded } = useSidebar();

  usePushSubscription(!!user);
  const [showWarning, setShowWarning] = useState(false);
  const [showTimeoutModal, setShowTimeoutModal] = useState(false);
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warnTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (sessionTimedOut) setShowTimeoutModal(true);
  }, [sessionTimedOut]);

  const attemptRefresh = async (): Promise<boolean> => {
    try {
      const res = await fetch('/api/v1/auth/refresh', { method: 'POST', credentials: 'include' });
      if (!res.ok) return false;
      const data = (await res.json()) as {
        accessToken: string;
        user: {
          id: string;
          email: string;
          name: string;
          avatarUrl?: string | null;
          permissions: string[];
          licensingAuthorities?: string[];
        };
      };
      useAuthStore.getState().setSession(
        {
          id: data.user.id,
          email: data.user.email,
          name: data.user.name,
          avatarUrl: data.user.avatarUrl,
        },
        data.user.permissions,
        data.accessToken,
        false,
        data.user.licensingAuthorities ?? [],
      );
      return true;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    const resetTimers = () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      if (warnTimer.current) clearTimeout(warnTimer.current);
      setShowWarning(false);
      warnTimer.current = setTimeout(() => setShowWarning(true), IDLE_TIMEOUT_MS - WARN_BEFORE_MS);
      idleTimer.current = setTimeout(async () => {
        const stillValid = await attemptRefresh();
        if (stillValid) {
          resetTimers();
        } else {
          // Capture the redirect target before clearSession() wipes licensingAuthorities.
          const redirectPath = getLoginRedirectPath(useAuthStore.getState().licensingAuthorities);
          useAuthStore.setState({ timeoutRedirectPath: redirectPath });
          clearSession();
          setShowTimeoutModal(true);
        }
      }, IDLE_TIMEOUT_MS);
    };
    const events = ['click', 'keydown', 'scroll', 'mousemove'];
    events.forEach((e) => window.addEventListener(e, resetTimers, { passive: true }));
    resetTimers();
    return () => {
      events.forEach((e) => window.removeEventListener(e, resetTimers));
      if (idleTimer.current) clearTimeout(idleTimer.current);
      if (warnTimer.current) clearTimeout(warnTimer.current);
    };
  }, [clearSession]);

  const stayLoggedIn = async () => {
    await attemptRefresh();
    setShowWarning(false);
  };

  const handleTimeoutConfirm = async () => {
    setShowTimeoutModal(false);
    const redirectPath = useAuthStore.getState().timeoutRedirectPath;
    clearSession();
    try {
      await fetch('/api/v1/auth/logout', { method: 'POST', credentials: 'include' });
    } catch {
      // ignore — still redirect
    }
    window.location.href = redirectPath;
  };

  return (
    <div className="min-h-screen bg-gray-50 xl:flex">
      <CommandPalette />
      <Sidebar />
      <div
        className={cn(
          'flex flex-1 flex-col transition-all duration-300 ease-in-out',
          isExpanded ? 'lg:ml-64' : 'lg:ml-20',
        )}
      >
        <Header />
        <main className="pt-16">
          {noPadding ? (
            children
          ) : (
            <div className="p-6 md:p-8">
              <Breadcrumb />
              {children}
            </div>
          )}
        </main>
      </div>

      {/* Idle warning */}
      {showWarning && (
        <div className="fixed bottom-6 right-6 z-[999] max-w-sm rounded-2xl border border-warning-300 bg-warning-50 p-4 shadow-theme-lg">
          <p className="mb-3 text-sm font-medium text-gray-800">
            Your session will expire in 2 minutes due to inactivity.
          </p>
          <button
            type="button"
            onClick={stayLoggedIn}
            className="rounded-lg bg-warning-500 px-4 py-2 text-sm font-semibold text-white hover:bg-warning-600 transition-colors"
          >
            Stay logged in
          </button>
        </div>
      )}

      {/* Timeout modal */}
      {showTimeoutModal && (
        <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-foreground/15 backdrop-blur-md">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-theme-xl">
            <h2 className="mb-2 text-lg font-bold text-gray-900">Session expired</h2>
            <p className="mb-5 text-sm text-gray-500">
              Your session has expired due to inactivity. Please sign in again.
            </p>
            <button
              type="button"
              onClick={handleTimeoutConfirm}
              className="w-full rounded-lg bg-brand-500 py-2.5 text-sm font-semibold text-white hover:bg-brand-600 transition-colors"
            >
              Sign in again
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function AppLayout({ children, noPadding }: AppLayoutProps) {
  return (
    <SidebarProvider>
      <LayoutContent noPadding={noPadding}>{children}</LayoutContent>
    </SidebarProvider>
  );
}
